// MACHINERY V2 (Stroje) — Fleet V1 body: big cards, 3-up (M2.2; reduced to a
// body at M2.3).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.2. Reference impl:
// design_handoff_machinery_v3/machinery-v1-big.jsx (V1Body).
//
// WHAT CHANGED AT M2.3 AND WHY: this used to be the route element and owned the
// shell + the data hook. When Fleet V2 arrived, keeping the hook here would have
// meant two telemetry pollers (one per view). The shell moved to FleetPage.tsx,
// which owns `useFleetPageData` — and therefore the single 60 s
// `useFleetTelemetry` poll — and swaps this body against FleetV2Page. This file
// keeps the name the ticket assigns it and is now purely presentational: it
// receives everything, fetches nothing, derives nothing.
//
// THE LIVE FLEET STRIP IS NOT HERE — IT MOVED BACK TO THE SHELL (review F1).
// Putting it in this body placed it inside the shell's
// `isLoading -> visibleCount === 0 -> view` chain, so it only rendered in the
// final else. Two real consequences: toggling the "Alarm" chip when nothing is
// in alarm drove visibleCount to 0, the empty-state Notice replaced the body,
// and THE STRIP HOLDING THE CHIP JUST CLICKED VANISHED — recoverable only via
// the rail. And because customerMachinerySlice sets loading=true for
// createMachine/restoreMachine too, a Duplicate flashed the whole strip out to
// "Loading machines…". A filter control may not be unmounted by the result of
// its own filter. The shell now renders it on its own terms (active scope, V1
// view) ABOVE the branch, so it stays put while the body swaps beneath it.
//
// It stays V1-only because the artboard puts it in V1Body: Fleet V2 carries the
// same information per section in its group health headers. The status FILTERS
// remain reachable in both views from the FilterRail's "Stav" section — the same
// `fleetStatusFilters` state — so switching view never loses a filter, only the
// second way of setting it.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import { Machine } from "../../models/models";
import { MachineryV2ReadinessPresentation } from "../readiness/readinessPresentation";
import FleetGrid from "./FleetGrid";
import { FleetMenuAction } from "./FleetCardMenu";
import { MachineryV2CardState, MachineryV2Telemetry } from "./cardStates";
import { FleetGroups } from "./fleetSelectors";

export interface FleetV1PageProps {
  /** Pre-grouped by the shell (useFleetPageData.sections) — the SAME map Fleet
   *  V2 renders as rows, so both views section identically. */
  sections: FleetGroups;
  /** Part of the shell's shared body props. V1 does not read it — the cards
   *  derive their own presentation — but Fleet V2's group health headers do,
   *  so the shell passes one object to both bodies. */
  states: Map<string, MachineryV2CardState>;
  telemetry: Map<string, MachineryV2Telemetry>;
  readiness: Map<string, MachineryV2ReadinessPresentation>;
  newMachineId: string | null;
  collapsedGroups: Set<string>;
  onToggleGroup: (name: string) => void;
  buildActions: (m: Machine) => FleetMenuAction[];
  onOpenProfile: (m: Machine) => void;
  onOpenReadiness: (m: Machine) => void;
}

const FleetV1Page: React.FC<FleetV1PageProps> = ({
  sections, telemetry, readiness, newMachineId, collapsedGroups, onToggleGroup,
  buildActions, onOpenProfile, onOpenReadiness,
}) => (
  <FleetGrid
    groups={sections}
    telemetry={telemetry}
    readiness={readiness}
    newMachineId={newMachineId}
    collapsedGroups={collapsedGroups}
    onToggleGroup={onToggleGroup}
    buildActions={buildActions}
    onOpenProfile={onOpenProfile}
    onOpenReadiness={onOpenReadiness}
  />
);

export default FleetV1Page;
